import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { getAppointmentTypeLabel } from './appointmentTypes';
import { doc, setDoc, collection, addDoc, deleteDoc, query, where, getDocs, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import QRCode from 'qrcode';

// Helper function to convert 24-hour format to 12-hour format
const formatTime12Hour = (time24) => {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Helper function to format dates in DD/MM/YYYY format (using local timezone)
const formatDate = (date) => {
  if (!date) return '';
  let d;
  if (date.toDate) {
    // Firestore timestamp
    d = date.toDate();
  } else if (date instanceof Date) {
    // Already a Date object
    d = date;
  } else {
    // String or timestamp
    d = new Date(date);
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
};

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderDetails = location.state?.orderDetails || location.state;
  const isTakingTrade = location.state?.isTakingTrade || false;
  const tradeOrder = location.state?.tradeOrder || null;
  const [currentUser, setCurrentUser] = useState(null);
  
  // Timer state from appointment page
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  

  const shouldClearSessionStorage = useRef(false);
  const timeLeftRef = useRef(null);
  const isRedirecting = useRef(false);
  const hasCheckedSessionStorage = useRef(false);
  
  // Session tracking refs
  const sessionId = useRef(null);
  const isSessionActive = useRef(false);
  const sessionCheckInterval = useRef(null);
  const storageEventListener = useRef(null);
  
  // Generate unique page load ID to detect refreshes
  const pageLoadId = useRef(`page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Flag to prevent multiple redirects
  const hasRedirected = useRef(false);
  
  // Validate and apply coupon




  
  // Helper function to handle redirect cleanup
  const handleRedirectCleanup = () => {
    if (hasRedirected.current) {
      console.log('Already redirected, skipping additional redirect');
      return true; // Already redirected
    }
    
    hasRedirected.current = true;
    
    // Clean up sessions immediately before redirect
    if (auth.currentUser) {
      const currentSessionKey = `payment_session_${auth.currentUser.uid}`;
      const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
      
      // Clean up browser session
      sessionStorage.removeItem(currentSessionKey);
      console.log('Browser session cleaned up before redirect');
      
      // Clean up global session if this tab owns it
      const globalSession = localStorage.getItem(globalSessionKey);
      if (globalSession) {
        try {
          const globalSessionData = JSON.parse(globalSession);
          if (globalSessionData.ownerTabId === pageLoadId.current && globalSessionData.page === 'payment') {
            localStorage.removeItem(globalSessionKey);
            console.log('Global payment session cleaned up by owner tab before redirect');
          }
        } catch (error) {
          console.warn('Error parsing global session before redirect:', error);
          localStorage.removeItem(globalSessionKey);
        }
      }
    }
    
    // Stop all intervals and cleanup to prevent further conflicts
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
    
    // Reset session tracking
    isSessionActive.current = false;
    sessionId.current = null;
    
    return false; // Not redirected yet
  };

  // Force cleanup function for payment sessions
  const forceCleanupPaymentSession = () => {
    console.log('Force cleanup triggered for payment session');
    
    if (auth.currentUser) {
      const currentSessionKey = `payment_session_${auth.currentUser.uid}`;
      const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
      
      // Clean up browser session
      sessionStorage.removeItem(currentSessionKey);
      console.log('Browser payment session force cleaned up');
      
      // Track that payment session was recently cleaned up (prevent recreation)
      sessionStorage.setItem('recently_cleaned_up_payment', JSON.stringify(Date.now()));
      console.log('Marked payment session as recently cleaned up');
      
      // Force clean up global session regardless of ownership
      localStorage.removeItem(globalSessionKey);
      console.log('Global payment session force cleaned up');
      
      // Track that global session was recently cleaned up (prevent recreation)
      sessionStorage.setItem('recently_cleaned_up_global', JSON.stringify(Date.now()));
      console.log('Marked global session as recently cleaned up');
    }
    
    // Reset session tracking
    isSessionActive.current = false;
    sessionId.current = null;
    
    // Stop intervals
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
  };

  // Make force cleanup available globally
  useEffect(() => {
    window.forceCleanupPaymentSession = forceCleanupPaymentSession;
    return () => {
      delete window.forceCleanupPaymentSession;
    };
  }, []);
  
  // Debug timer state changes (reduced frequency)
  useEffect(() => {
    // Only log significant timer state changes
    if (timeLeft === null || timeLeft % 60 === 0) {
      console.log('Timer state changed:', { timeLeft, timerActive });
    }
  }, [timeLeft, timerActive]);
  
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });

  // Get current user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Session management functions
  const createUserSession = async () => {
    if (!auth.currentUser) {
      console.log('User not authenticated, skipping Firestore session creation');
      return null;
    }
    
    try {
      // Check if user is still authenticated before making Firestore calls
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('User authentication lost, skipping Firestore session creation');
        return null;
      }
      
      const sessionData = {
        userId: currentUser.uid,
        page: 'payment',
        timestamp: new Date(),
        lastActivity: new Date(),
        sessionId: Math.random().toString(36).substr(2, 9)
      };
      
      const sessionRef = doc(db, 'userSessions', currentUser.uid);
      await setDoc(sessionRef, sessionData);
      
      sessionId.current = sessionData.sessionId;
      isSessionActive.current = true;
      
      console.log('Payment user session created in Firestore:', sessionData.sessionId);
      return sessionData.sessionId;
    } catch (error) {
      // Log the error but don't throw - this is expected if user doesn't have permissions
      console.warn('Could not create payment user session in Firestore (permission issue):', error.message);
      // Return null but don't fail - browser session will still work
      return null;
    }
  };

  const updateSessionActivity = async () => {
    if (!auth.currentUser || !isSessionActive.current) return;
    
    try {
      // Check if user is still authenticated before making Firestore calls
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('User authentication lost, skipping session activity update');
        return;
      }
      
      const sessionRef = doc(db, 'userSessions', currentUser.uid);
      await setDoc(sessionRef, {
        lastActivity: new Date()
      }, { merge: true });
    } catch (error) {
      // Log the error but don't throw - this is expected if user doesn't have permissions
      console.warn('Could not update payment session activity (permission issue):', error.message);
      // This is okay - browser session will continue to work
    }
  };

  const deleteUserSession = async () => {
    // Clean up browser session immediately (always do this)
    if (auth.currentUser) {
      const currentSessionKey = `payment_session_${auth.currentUser.uid}`;
      const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
      
      sessionStorage.removeItem(currentSessionKey);
      localStorage.removeItem(globalSessionKey);
      
      console.log('Browser and global payment sessions cleaned up');
    }
    
    // Clean up local state
    isSessionActive.current = false;
    sessionId.current = null;
    
    // Only try to delete from Firestore if user is authenticated
    if (!auth.currentUser) {
      console.log('User not authenticated, skipping Firestore session deletion');
      return;
    }
    
    try {
      // Check if user is still authenticated before making Firestore calls
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('User authentication lost, skipping Firestore session deletion');
        return;
      }
      
      const sessionRef = doc(db, 'userSessions', currentUser.uid);
      await deleteDoc(sessionRef);
      console.log('Payment user session deleted from Firestore');
    } catch (error) {
      // Log the error but don't throw - this is expected if user doesn't have permissions
      console.warn('Could not delete payment user session from Firestore (permission issue):', error.message);
      // This is okay - the browser session is already cleaned up
    }
  };

  const checkForExistingSession = async () => {
    if (!auth.currentUser) {
      console.log('User not authenticated, skipping Firestore session check');
      return false;
    }
    
    try {
      // Check if user is still authenticated before making Firestore calls
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('User authentication lost, skipping Firestore session check');
        return false;
      }
      
      const sessionQuery = query(collection(db, 'userSessions'), where('userId', '==', currentUser.uid));
      const sessionSnapshot = await getDocs(sessionQuery);
      
      if (!sessionSnapshot.empty) {
        const sessionData = sessionSnapshot.docs[0].data();
        const lastActivity = sessionData.lastActivity?.toDate();
        const now = new Date();
        
        // If session is older than 5 minutes, consider it stale
        if (lastActivity && (now - lastActivity) > 5 * 60 * 1000) {
          await deleteUserSession();
          return false;
        }
        
        // If session is for appointment page, allow payment page
        if (sessionData.page === 'appointment') {
          return false;
        }
        
        // If session is for payment page and active, prevent access
        if (sessionData.page === 'payment' && sessionData.sessionId !== sessionId.current) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      // Log the error but don't throw - this is expected if user doesn't have permissions
      console.warn('Could not check for existing payment session (permission issue):', error.message);
      // Return false to allow access - browser session will handle conflicts
      return false;
    }
  };

  // REMOVED: Active booking session deletion - no longer needed since we use direct database checking

  // Timer effect
  useEffect(() => {
    let interval = null;
    
    if (timerActive && timeLeftRef.current > 0) {
      console.log('Starting timer with timeLeft:', timeLeftRef.current);
      interval = setInterval(() => {
        timeLeftRef.current = timeLeftRef.current - 1;
        setTimeLeft(timeLeftRef.current);
        
        // Only log every 30 seconds to reduce console noise
        if (timeLeftRef.current % 30 === 0) {
          console.log('Timer tick:', timeLeftRef.current);
        }
        
        if (timeLeftRef.current <= 0) {
          setTimerActive(false);
          shouldClearSessionStorage.current = true; // Mark for cleanup
          isRedirecting.current = true; // Mark that we're redirecting
          console.log('Timer expired, redirecting...');
          
          // Delete user session (non-blocking)
          deleteUserSession().catch(error => {
            console.warn('Error deleting user session:', error);
          });
          
          if (isTakingTrade) {
            // For trade orders, redirect to appointment trade page
            console.log('Trade timer expired, redirecting to appointment trade page');
            navigate('/appointment-swap', { 
              state: { 
                expired: true,
                message: 'Trade time expired. The appointment is no longer available.'
              } 
            });
          } else {
            // For regular orders, redirect to appointment page
            console.log('Regular timer expired, redirecting to appointment page');
          
          // Extract service data from orderDetails for appointment page
          const serviceData = {
            name: orderDetails.name,
            price: orderDetails.price,
            description: orderDetails.description
          };
          
          // Time expired - redirect to appointment page with reset flag
          navigate('/appointment', { 
            state: { 
              expired: true,
              resetBooking: true,
              serviceData: serviceData,
              orderDetails: orderDetails 
            } 
          });
          }
          return;
        }
        
        // No need to update sessionStorage since page refresh will cancel the session
      }, 1000);
    }
    
    return () => {
      if (interval) {
        console.log('Clearing timer interval');
        clearInterval(interval);
      }
    };
  }, [timerActive, navigate, orderDetails]); // Removed timeLeft from dependencies

  // Initialize timer state from sessionStorage first, then orderDetails
  useEffect(() => {
    // Only run this effect once
    if (hasCheckedSessionStorage.current) {
      console.log('SessionStorage already checked, skipping...');
      return;
    }
    
    hasCheckedSessionStorage.current = true;
    
    console.log('=== PAYMENT PAGE MOUNT ===');
    console.log('Component mounted - checking sessionStorage...');
    console.log('Initial isRedirecting state:', isRedirecting.current);
    console.log('orderDetails:', orderDetails);
    
    // Reset redirecting flag on component mount
    isRedirecting.current = false;
    
    const savedTimer = sessionStorage.getItem('paymentTimer');
    console.log('Saved timer data:', savedTimer);
    
    if (savedTimer) {
      try {
        const timerData = JSON.parse(savedTimer);
        console.log('Page refresh detected - timer data:', timerData);
        
      shouldClearSessionStorage.current = true;
      isRedirecting.current = true; // Mark that we're redirecting
      sessionStorage.removeItem('paymentTimer');
      
      // Delete user session (non-blocking)
      deleteUserSession().catch(error => {
        console.warn('Error deleting user session on refresh:', error);
      });
        
        if (timerData.isTradeOrder) {
          // For trade orders, redirect to appointment trade page
          console.log('Trade order refresh detected - redirecting to appointment trade page');
          navigate('/appointment-swap', { 
            state: { 
              expired: true,
              message: 'Page refresh detected. Please try taking the appointment again.'
            } 
          });
        } else {
          // For regular orders, redirect to appointment page
          console.log('Regular order refresh detected - redirecting to appointment page');
      
      // Extract service data from orderDetails for appointment page
      const serviceData = {
        name: orderDetails.name,
        price: orderDetails.price,
        description: orderDetails.description
      };
      
      // Redirect to appointment page to restart booking process
      navigate('/appointment', { 
        state: { 
          expired: true,
          resetBooking: true,
          serviceData: serviceData,
          orderDetails: orderDetails 
        } 
      });
        }
      } catch (error) {
        console.error('Error parsing saved timer data on refresh:', error);
        sessionStorage.removeItem('paymentTimer');
        navigate('/');
      }
    } else {
      console.log('No saved timer data found in sessionStorage - normal navigation');
      // Normal navigation - ensure redirecting flag is false
      isRedirecting.current = false;
    }
  }, []); // Run only once on component mount

  // Save timer state to sessionStorage when orderDetails are available (initial load only)
  useEffect(() => {
    // Skip if we're redirecting due to page refresh
    if (isRedirecting.current) {
      console.log('Skipping orderDetails initialization - redirecting due to page refresh');
      return;
    }
    
    // Check for existing timer in sessionStorage first
    const savedTimer = sessionStorage.getItem('paymentTimer');
    if (savedTimer) {
      try {
        const timerData = JSON.parse(savedTimer);
        console.log('SessionStorage timer data exists:', timerData);
        
        // Restore timer from sessionStorage
        setTimeLeft(timerData.timeLeft);
        setTimerActive(timerData.timerActive);
        timeLeftRef.current = timerData.timeLeft;
        
        console.log('Timer restored from sessionStorage:', timerData.timeLeft);
      return;
      } catch (error) {
        console.error('Error parsing saved timer data:', error);
        sessionStorage.removeItem('paymentTimer');
      }
    }
    
    // Initialize timer for trade orders
    if (isTakingTrade && tradeOrder && !timeLeft && !timerActive) {
      console.log('Initializing timer for trade order...');
      
      // Set timer based on appointment type
      let initialTime;
      if (tradeOrder.appointmentType === 'free') {
        initialTime = 30; // 30 seconds for free appointments
        console.log('Setting 30-second timer for free appointment trade');
      } else {
        initialTime = 5 * 60; // 5 minutes for special appointments
        console.log('Setting 5-minute timer for special appointment trade');
      }
      
      // Initialize timer
      setTimeLeft(initialTime);
      setTimerActive(true);
      timeLeftRef.current = initialTime;
      
      // Save to sessionStorage for persistence
      const timerData = {
        timeLeft: initialTime,
        timerActive: true,
        isTradeOrder: true,
        tradeOrder: tradeOrder
      };
      sessionStorage.setItem('paymentTimer', JSON.stringify(timerData));
      console.log('Trade timer initialized:', initialTime, 'seconds');
    }
    
    // Handle regular orderDetails timer (existing logic)
    if (orderDetails?.timeLeft && orderDetails?.timerActive && !timeLeft && !timerActive) {
      console.log('Initial load - initializing timer from orderDetails...');
      console.log('orderDetails.timeLeft:', orderDetails.timeLeft);
      console.log('orderDetails.timerActive:', orderDetails.timerActive);
      
      // Initial load from orderDetails
      setTimeLeft(orderDetails.timeLeft);
      setTimerActive(orderDetails.timerActive);
      timeLeftRef.current = orderDetails.timeLeft; // Set the ref
      
      // Save to sessionStorage for persistence
      const timerData = {
        timeLeft: orderDetails.timeLeft,
        timerActive: orderDetails.timerActive,
        orderDetails: orderDetails
      };
      sessionStorage.setItem('paymentTimer', JSON.stringify(timerData));
      console.log('Timer initialized from orderDetails:', orderDetails.timeLeft);
      console.log('Saved to sessionStorage:', timerData);
    }
  }, [orderDetails]); // Only depend on orderDetails, not timer state

  // Cleanup timer data when component unmounts
  useEffect(() => {
    return () => {
      console.log('Component unmounting - shouldClearSessionStorage:', shouldClearSessionStorage.current);
      if (shouldClearSessionStorage.current) {
        console.log('Clearing sessionStorage on unmount');
        sessionStorage.removeItem('paymentTimer');
      } else {
        console.log('Keeping sessionStorage - timer still active');
      }
    };
  }, []); // No dependencies - only runs on unmount

  // Handle page unload and visibility changes
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clean up browser session immediately
      if (auth.currentUser) {
        const currentSessionKey = `payment_session_${auth.currentUser.uid}`;
        sessionStorage.removeItem(currentSessionKey);
      }
      
      if (isSessionActive.current) {
        deleteUserSession();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Clean up browser session when tab becomes hidden
        if (auth.currentUser) {
          const currentSessionKey = `payment_session_${auth.currentUser.uid}`;
          sessionStorage.removeItem(currentSessionKey);
        }
        
        if (isSessionActive.current) {
          deleteUserSession();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Session management effect - runs on component mount
  useEffect(() => {
    const initializeSession = async () => {
      if (!auth.currentUser) return;
      
      // IMMEDIATE CHECK: Use localStorage for persistent conflict detection
      const currentSessionKey = `payment_session_${auth.currentUser.uid}`;
      const appointmentSessionKey = `appointment_session_${auth.currentUser.uid}`;
      const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
      
      // Check for global booking session first (persistent across browser restarts)
      const existingGlobalSession = localStorage.getItem(globalSessionKey);
      if (existingGlobalSession) {
        try {
          const globalSessionData = JSON.parse(existingGlobalSession);
          const now = Date.now();
          
          // Ultra aggressive cleanup: 2 seconds instead of 10
          if (now - globalSessionData.timestamp > 2 * 1000) {
            localStorage.removeItem(globalSessionKey);
            console.log('Stale global session removed');
          } else if (globalSessionData.page === 'payment' && globalSessionData.sessionId !== sessionId.current) {
            // Another payment session is active
            console.log('Global payment session conflict detected');
            
            // Force cleanup any existing sessions before showing error
            if (window.forceCleanupPaymentSession) {
              window.forceCleanupPaymentSession();
            }
            
            // Add a small delay to ensure cleanup completes
            setTimeout(() => {
              if (handleRedirectCleanup()) {
                return;
              }
              alert('Another payment page is already open. Try again in 10 seconds.');
              navigate('/');
            }, 100); // 100ms delay to ensure cleanup
            return;
          } else if (globalSessionData.page === 'appointment') {
            // Appointment session is active
            console.log('Global appointment session conflict detected');
            
            // Force cleanup any existing sessions before showing error
            if (window.forceCleanupPaymentSession) {
              window.forceCleanupPaymentSession();
            }
            
            // Add a small delay to ensure cleanup completes
            setTimeout(() => {
              if (handleRedirectCleanup()) {
                return;
              }
              alert('An appointment page is already open. Redirecting to home page.');
              navigate('/');
            }, 100); // 100ms delay to ensure cleanup
            return;
          }
        } catch (error) {
          console.log('Invalid global session data, removing');
          localStorage.removeItem(globalSessionKey);
        }
      }
      
      // Check for appointment page conflict (sessionStorage - current session only)
      const existingAppointmentSession = sessionStorage.getItem(appointmentSessionKey);
      if (existingAppointmentSession) {
        try {
          const appointmentSessionData = JSON.parse(existingAppointmentSession);
          const now = Date.now();
          const age = now - appointmentSessionData.timestamp;
          
          console.log('Found appointment session:', appointmentSessionData.page, 'age:', Math.round(age/1000), 'seconds');
          
          // Ultra aggressive cleanup: 2 seconds instead of 5, or if age is 0 or 1 (just created)
          if (age > 2 * 1000 || age === 0 || age === 1000) {
            console.log('Appointment session is stale or just created (0-1 seconds), removing');
            sessionStorage.removeItem(appointmentSessionKey);
          } else {
            // Additional validation: ensure session has required fields
            if (appointmentSessionData.page && appointmentSessionData.timestamp && appointmentSessionData.page === 'appointment') {
              console.log('Active appointment session found - BLOCKING');
              
              // Force cleanup any existing sessions before showing error
              if (window.forceCleanupPaymentSession) {
                window.forceCleanupPaymentSession();
              }
              
              // Add a small delay to ensure cleanup completes
              setTimeout(() => {
                if (handleRedirectCleanup()) {
                  return;
                }
                alert('An appointment page is already open. Redirecting to home page.');
                navigate('/');
              }, 100); // 100ms delay to ensure cleanup
              return;
            } else {
              console.log('Invalid appointment session data, removing');
              sessionStorage.removeItem(appointmentSessionKey);
            }
          }
        } catch (error) {
          // Invalid session data, remove it
          sessionStorage.removeItem(appointmentSessionKey);
        }
      }
      
      // Check for existing payment session
      const existingSession = sessionStorage.getItem(currentSessionKey);
      
      if (existingSession) {
        try {
          const sessionData = JSON.parse(existingSession);
          const now = Date.now();
          const age = now - sessionData.timestamp;
          
          console.log('Found payment session:', sessionData.page, 'age:', Math.round(age/1000), 'seconds');
          
          // Ultra aggressive cleanup: 2 seconds instead of 5, or if age is 0 or 1 (just created)
          if (age > 2 * 1000 || age === 0 || age === 1000) {
            console.log('Payment session is stale or just created (0-1 seconds), removing');
            sessionStorage.removeItem(currentSessionKey);
          } else {
            // Additional validation: ensure session has required fields
            if (sessionData.page && sessionData.timestamp && sessionData.page === 'payment') {
              console.log('Active payment session found - BLOCKING');
              
              // Force cleanup any existing sessions before showing error
              if (window.forceCleanupPaymentSession) {
                window.forceCleanupPaymentSession();
              }
              
              // Add a small delay to ensure cleanup completes
              setTimeout(() => {
                if (handleRedirectCleanup()) {
                  return;
                }
                alert('Another payment page is already open. Try again in 10 seconds.');
                navigate('/');
              }, 100); // 100ms delay to ensure cleanup
              return;
            } else {
              console.log('Invalid payment session data, removing');
              sessionStorage.removeItem(currentSessionKey);
            }
          }
        } catch (error) {
          // Invalid session data, remove it
          sessionStorage.removeItem(currentSessionKey);
        }
      }
      
      // Check if payment session was recently cleaned up (prevent recreation)
      const recentlyCleanedUpPayment = sessionStorage.getItem('recently_cleaned_up_payment');
      if (recentlyCleanedUpPayment) {
        const cleanupTime = JSON.parse(recentlyCleanedUpPayment);
        const timeSinceCleanup = Date.now() - cleanupTime;
        
        // If cleanup was less than 3 seconds ago, don't create new session
        if (timeSinceCleanup < 3000) {
          console.log('Payment session was recently cleaned up, not creating new session');
          sessionStorage.removeItem('recently_cleaned_up_payment');
          return;
        }
      }
      
      // Create browser session immediately (this is the primary session management)
      const browserSession = {
        sessionId: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
        page: 'payment',
        userId: auth.currentUser.uid,
        pageLoadId: pageLoadId.current
      };
      sessionStorage.setItem(currentSessionKey, JSON.stringify(browserSession));
      sessionId.current = browserSession.sessionId;
      isSessionActive.current = true;
      
      // Create global session (persistent across browser restarts)
      const newGlobalSession = {
        sessionId: browserSession.sessionId,
        timestamp: Date.now(),
        page: 'payment',
        userId: auth.currentUser.uid,
        browserSessionId: browserSession.sessionId,
        ownerTabId: pageLoadId.current,
        lastHeartbeat: Date.now()
      };
      localStorage.setItem(globalSessionKey, JSON.stringify(newGlobalSession));
      
      console.log('Browser payment session created:', browserSession.sessionId);
      console.log('Global payment session created:', newGlobalSession.sessionId);
      
      // Now check Firestore for additional conflicts (non-blocking)
      try {
        const hasExistingSession = await checkForExistingSession();
        if (hasExistingSession) {
          // Clean up browser session
          sessionStorage.removeItem(currentSessionKey);
          isSessionActive.current = false;
          sessionId.current = null;
          
          if (handleRedirectCleanup()) {
            return;
          }
          alert('Another payment page is already open. Try again in 10 seconds.');
          navigate('/');
          return;
        }
        
        // Try to create Firestore session (optional - browser session already works)
        await createUserSession();
      } catch (error) {
        console.warn('Firestore session operations failed, continuing with browser session only:', error.message);
        // This is fine - browser session is already created and working
      }
      
      // Set up activity tracking
      sessionCheckInterval.current = setInterval(() => {
        updateSessionActivity();
        // Also update browser session
        const updatedSession = {
          ...browserSession,
          timestamp: Date.now()
        };
        sessionStorage.setItem(currentSessionKey, JSON.stringify(updatedSession));
      }, 30000); // Update every 30 seconds
      
      // Listen for storage changes (other tabs) - IMMEDIATE conflict detection
      const handleStorageChange = (e) => {
        if (e.key === currentSessionKey && e.newValue) {
          try {
            const newSessionData = JSON.parse(e.newValue);
            console.log('Storage change detected:', newSessionData.sessionId, 'vs', browserSession.sessionId);
            
            // If another tab created a session, check if it's newer
            if (newSessionData.sessionId !== browserSession.sessionId) {
              console.log('Conflict detected - another payment page opened');
              if (handleRedirectCleanup()) {
                return;
              }
              alert('Another payment page has been opened. Redirecting to home page.');
              navigate('/');
            }
          } catch (error) {
            console.warn('Error parsing storage change:', error);
          }
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      
      // Store the event listener for cleanup
      storageEventListener.current = handleStorageChange;
    };
    
    initializeSession();
    
    return () => {
      console.log('Payment component unmounting - cleaning up sessions IMMEDIATELY');
      
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
      
      // Remove storage event listener
      if (storageEventListener.current) {
        window.removeEventListener('storage', storageEventListener.current);
      }
      
      // IMMEDIATE cleanup of browser and global sessions
      if (auth.currentUser) {
        const currentSessionKey = `payment_session_${auth.currentUser.uid}`;
        const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
        
        // Always clean up browser session immediately
        sessionStorage.removeItem(currentSessionKey);
        console.log('Browser session cleaned up immediately on unmount');
        
        // Clean up global session immediately (more aggressive)
        const globalSession = localStorage.getItem(globalSessionKey);
        if (globalSession) {
          try {
            const globalSessionData = JSON.parse(globalSession);
            // Clean up if this tab owns it OR if it's a payment session (more aggressive)
            if (globalSessionData.ownerTabId === pageLoadId.current || globalSessionData.page === 'payment') {
              localStorage.removeItem(globalSessionKey);
              console.log('Global payment session cleaned up immediately on unmount');
              
              // Track that global session was recently cleaned up (prevent recreation)
              sessionStorage.setItem('recently_cleaned_up_global', JSON.stringify(Date.now()));
              console.log('Marked global session as recently cleaned up on unmount');
            } else {
              console.log('Global session not cleaned up - different owner and not payment');
            }
          } catch (error) {
            // If parsing fails, clean it up anyway
            localStorage.removeItem(globalSessionKey);
            console.log('Invalid global session cleaned up on unmount');
            
            // Track that global session was recently cleaned up (prevent recreation)
            sessionStorage.setItem('recently_cleaned_up_global', JSON.stringify(Date.now()));
            console.log('Marked global session as recently cleaned up on unmount');
          }
        }
      }
      
      // Clean up Firestore session (non-blocking)
      deleteUserSession().catch(error => {
        console.warn('Error deleting user session on unmount:', error);
      });
    };
  }, []);

  // Redirect if no order details
  useEffect(() => {
    if (!orderDetails) {
      alert('No order details found. Please select a service first.');
      navigate('/');
    }
  }, [orderDetails, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🚀 Payment form submitted');
    console.log('📋 Order details:', orderDetails);
    console.log('👤 Current user:', currentUser?.uid);
    
    if (!currentUser) {
      alert('Please sign in to complete your order.');
      navigate('/auth');
      return;
    }
    
    // Check if user is still authenticated before proceeding
    if (!auth.currentUser) {
      alert('Authentication expired. Please sign in again.');
      navigate('/auth');
      return;
    }

    try {
      // Generate unique order ID
      const orderId = `ORDER-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      
      // Handle trade order processing
      if (isTakingTrade && tradeOrder) {
        console.log('🔄 Processing trade order:', tradeOrder.originalOrderId);
        
        try {
          // Use secure backend function for order transfer
          const functions = getFunctions();
          const transferTradingOrder = httpsCallable(functions, 'transferTradingOrder');
          
          const transferResult = await transferTradingOrder({
            originalOrderId: tradeOrder.originalOrderId,
            paymentData: {
              cardholderName: paymentData.cardholderName
            },

          });
          
          console.log('✅ Secure order transfer completed:', transferResult.data);
          

          
          // Mark for cleanup and clear timer data from sessionStorage
          shouldClearSessionStorage.current = true;
          sessionStorage.removeItem('paymentTimer');
          
          // Clean up global session immediately (booking completed)
          if (auth.currentUser) {
            const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
            localStorage.removeItem(globalSessionKey);
            console.log('Global session cleaned up after successful trade payment');
          }
          
          // Delete user session (non-blocking)
          deleteUserSession().catch(error => {
            console.warn('Error deleting user session after successful trade payment:', error);
          });
          
          alert('Trade completed successfully! The appointment is now yours.');
          navigate('/account');
          return;
          
        } catch (tradeError) {
          console.error('❌ Error processing trade order:', tradeError);
          
          // Handle specific error types from backend
          if (tradeError.code === 'functions/failed-precondition') {
            alert('This order is no longer available for trade. It may have been taken by someone else or is too close to the appointment time.');
          } else if (tradeError.code === 'functions/permission-denied') {
            alert('You cannot trade your own order.');
          } else if (tradeError.code === 'functions/not-found') {
            alert('The original order was not found. It may have been already taken.');
          } else {
            alert('There was an error processing the trade. Please try again.');
          }
          return;
        }
      }
      
      // Regular order processing (non-trade)
      const orderData = {
        orderId: orderId,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        serviceName: orderDetails.name,
        serviceDescription: orderDetails.isDriveIn 
          ? orderDetails.description 
          : `${orderDetails.description} (${getAppointmentTypeLabel(orderDetails.appointmentType)}${orderDetails.specialIsFree ? ' - changed' : ''})`,
        price: orderDetails.specialIsFree ? 0 : parseFloat(orderDetails.price),
        status: 'confirmed',
        createdAt: new Date(),
        paymentMethod: 'credit_card',
        cardholderName: paymentData.cardholderName,
        // Add service type
        serviceType: orderDetails.isDriveIn ? 'drive-in' : 'appointment',
        // Add appointment information if available (only for appointments)
        location: orderDetails.isDriveIn ? null : orderDetails.location,
        appointmentDate: orderDetails.isDriveIn ? null : orderDetails.appointmentDate,
        appointmentTime: orderDetails.isDriveIn ? null : orderDetails.appointmentTime,
        appointmentDateTime: orderDetails.isDriveIn ? null : orderDetails.appointmentDateTime,
        appointmentType: orderDetails.isDriveIn ? null : orderDetails.appointmentType
      };

      console.log('📋 Creating order with data:', {
        orderId,
        userId: currentUser.uid,
        serviceName: orderDetails.name,
        appointmentType: orderDetails.appointmentType,
        specialIsFree: orderDetails.specialIsFree,
        price: orderData.price,
        status: orderData.status,
        isDriveIn: orderDetails.isDriveIn
      });

      // Generate QR code data (plain JSON)
      const bookedOnDate = new Date();
      const qrOrderData = {
        orderId: orderId,
        serviceName: orderDetails.name,
        serviceDescription: orderDetails.isDriveIn 
          ? orderDetails.description 
          : `${orderDetails.description} (${getAppointmentTypeLabel(orderDetails.appointmentType)}${orderDetails.specialIsFree ? ' - changed' : ''})`,
        price: orderDetails.specialIsFree ? 0 : orderDetails.price,
        userId: currentUser.uid,
        location: orderDetails.location,
        appointmentDate: orderDetails.appointmentDate,
        appointmentTime: formatTime12Hour(orderDetails.appointmentTime),
        bookedOn: `${bookedOnDate.getFullYear()}-${String(bookedOnDate.getMonth() + 1).padStart(2, '0')}-${String(bookedOnDate.getDate()).padStart(2, '0')}`
      };
      
      // Plain QR payload
      const qrData = JSON.stringify({ type: 'order', orderData: qrOrderData });

      // Generate QR code
      const qrCodeDataURL = await QRCode.toDataURL(qrData, {
        width: 200,
        margin: 2,
        color: {
          dark: '#1f2937',
          light: '#ffffff'
        }
      });

      // Save order to Firestore
      console.log('🔄 Attempting to save order to Firestore...');
      console.log('💰 Order price:', orderData.price, 'Type:', typeof orderData.price);
      
      // Special handling for $0 orders
      if (orderData.price === 0) {
        console.log('🎯 Processing $0 order - bypassing payment processing');
      }
      
      let orderRef;
      try {
        orderRef = await addDoc(collection(db, 'orders'), {
        ...orderData,
        qrCodeDataURL: qrCodeDataURL,
        qrData: qrData
      });
      
        console.log('✅ Order created successfully with ID:', orderRef.id);
        console.log('📋 Final order data saved:', orderData);
        
        // Verify the order was actually saved by reading it back
        console.log('🔍 Verifying order was saved...');
        const savedOrderDoc = await getDoc(orderRef);
        if (savedOrderDoc.exists()) {
          console.log('✅ Order verification successful - order exists in database');
        } else {
          console.log('❌ Order verification failed - order does not exist in database');
        }
      } catch (orderError) {
        console.error('❌ Error creating order:', orderError);
        console.error('❌ Error details:', {
          code: orderError.code,
          message: orderError.message,
          stack: orderError.stack
        });
        throw orderError; // Re-throw to prevent continuing
      }
      

        
        // Clear the special rebook state after successful order completion
        // This prevents multiple $0 bookings and marks the rebook state as used
        if (orderDetails.specialIsFree) {
          try {
            console.log('🔄 Clearing special rebook state after successful order completion...');
            const rebookStatesQuery = query(
              collection(db, 'specialRebookStates'),
              where('userId', '==', currentUser.uid),
              where('rebookType', '==', 'special')
            );
            const querySnapshot = await getDocs(rebookStatesQuery);
            if (!querySnapshot.empty) {
              await deleteDoc(doc(db, 'specialRebookStates', querySnapshot.docs[0].id));
              console.log('✅ Special rebook state cleared after successful order completion');
            } else {
              console.log('⚠️ No rebook state found to clear after order completion');
            }
          
          // Mark that we've used the rebook state
          const storageKey = `rebook_state_used_${currentUser.uid}`;
          sessionStorage.setItem(storageKey, 'true');
          console.log('💾 Stored rebook used state after order completion:', storageKey, '=', sessionStorage.getItem(storageKey));
        } catch (error) {
          console.error('❌ Error clearing rebook state after order completion:', error);
          // Still mark as used even if clearing fails
          const storageKey = `rebook_state_used_${currentUser.uid}`;
          sessionStorage.setItem(storageKey, 'true');
          console.log('💾 Stored rebook used state after order completion (error case):', storageKey, '=', sessionStorage.getItem(storageKey));
        }
      }

      console.log('Order created successfully:', orderRef.id);
      
      // Mark for cleanup and clear timer data from sessionStorage
      shouldClearSessionStorage.current = true;
      sessionStorage.removeItem('paymentTimer');
      
      // Clean up global session immediately (booking completed)
      if (auth.currentUser) {
        const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
        localStorage.removeItem(globalSessionKey);
        console.log('Global session cleaned up after successful payment');
      }
      
      // Delete user session (non-blocking)
      deleteUserSession().catch(error => {
        console.warn('Error deleting user session after successful payment:', error);
      });
      
      // Delete any active booking sessions for this user (non-blocking)
      if (auth.currentUser) {
        const activeSessionsQuery = query(
          collection(db, 'activeBookings'),
          where('userId', '==', auth.currentUser.uid)
        );
        getDocs(activeSessionsQuery).then(querySnapshot => {
          const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
          return Promise.all(deletePromises);
        }).then(() => {
          console.log('✅ All active booking sessions cleaned up after successful payment');
        }).catch(error => {
          console.warn('⚠️ Error deleting active booking sessions after successful payment:', error);
        });
      }
      
      alert('Payment processed successfully! Your order has been created.');
      navigate('/account');
      
    } catch (error) {
      console.error('❌ Error creating order:', error);
      console.error('❌ Error details:', error.code, error.message);
      alert('There was an error processing your order. Please try again.');
    }
  };

  return (
    <div className="payment-container" style={{
      maxWidth: '600px',
      margin: '2rem auto',
      padding: '2rem',
      background: '#fff',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }}>
      <h2 style={{
        textAlign: 'center',
        color: '#1f2937',
        marginBottom: '2rem',
        fontSize: '2rem'
      }}>{isTakingTrade ? 'Trade Payment' : 'Payment Information'}</h2>
      
      {/* Timer Display */}
      {timerActive && timeLeft !== null && (
        <div style={{
          background: timeLeft <= 60 ? '#fee2e2' : timeLeft <= 180 ? '#fef3c7' : '#f0f9ff',
          border: timeLeft <= 60 ? '1px solid #f87171' : timeLeft <= 180 ? '1px solid #f59e0b' : '1px solid #0ea5e9',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: 'bold',
            color: timeLeft <= 60 ? '#dc2626' : timeLeft <= 180 ? '#d97706' : '#0c4a6e',
            marginBottom: '0.5rem'
          }}>
            Time Remaining: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
          </div>
          <div style={{
            fontSize: '0.9rem',
            color: timeLeft <= 60 ? '#dc2626' : timeLeft <= 180 ? '#d97706' : '#0c4a6e'
          }}>
            {isTakingTrade 
              ? (timeLeft <= 30 ? 'Hurry! Complete your trade!' : 'Please complete your trade within the time limit')
              : (timeLeft <= 60 ? 'Hurry! Complete your payment!' : timeLeft <= 180 ? 'Complete your payment soon!' : 'Please complete your payment within the time limit')
            }
          </div>
        </div>
      )}
      
      {/* Order Summary */}
      {orderDetails && (
        <div style={{
          background: '#f9fafb',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            color: '#1f2937',
            marginBottom: '1rem',
            fontSize: '1.2rem'
          }}>Order Summary</h3>
          
          {/* Service Type Badge */}
          {orderDetails.isDriveIn && (
            <div style={{
              background: '#10b981',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              display: 'inline-block',
              marginBottom: '1rem'
            }}>
              🚗 Drive-In Service
            </div>
          )}
          
          {/* Trade Order Badge */}
          {isTakingTrade && (
            <div style={{
              background: '#8b5cf6',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              display: 'inline-block',
              marginBottom: '1rem'
            }}>
              🔄 Trading Appointment
            </div>
          )}
          
          {/* Appointment Type Badge */}
          {!orderDetails.isDriveIn && orderDetails.appointmentType && !isTakingTrade && (
            <div style={{
              background: orderDetails.appointmentType === 'free' ? '#3b82f6' : '#f59e0b',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              display: 'inline-block',
              marginBottom: '1rem'
            }}>
              {orderDetails.appointmentType === 'free' ? '📅 Normal Appointment' : '⭐ Special Appointment'}
            </div>
          )}
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem'
          }}>
            <span style={{ color: '#6b7280' }}>Service:</span>
            <span style={{ fontWeight: 'bold', color: '#1f2937' }}>
              {isTakingTrade ? tradeOrder.serviceName : orderDetails.name}
            </span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem'
          }}>
            <span style={{ color: '#6b7280' }}>Description:</span>
            <span style={{ color: '#1f2937' }}>
              {isTakingTrade ? tradeOrder.serviceDescription : orderDetails.description}
            </span>
          </div>
          
          {/* Show appointment type for appointments */}
          {(!orderDetails.isDriveIn && orderDetails.appointmentType && !isTakingTrade) || (isTakingTrade && tradeOrder.appointmentType) && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem'
            }}>
              <span style={{ color: '#6b7280' }}>Appointment Type:</span>
              <span style={{ color: '#1f2937' }}>
                {isTakingTrade 
                  ? getAppointmentTypeLabel(tradeOrder.appointmentType)
                  : getAppointmentTypeLabel(orderDetails.appointmentType)
                }
              </span>
            </div>
          )}
          
          {/* Show appointment details only if not drive-in */}
          {(!orderDetails.isDriveIn && orderDetails.location && !isTakingTrade) || (isTakingTrade && tradeOrder.location) && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem'
            }}>
              <span style={{ color: '#6b7280' }}>Location:</span>
              <span style={{ color: '#1f2937' }}>
                {isTakingTrade ? tradeOrder.location : orderDetails.location}
              </span>
            </div>
          )}
          {(!orderDetails.isDriveIn && orderDetails.appointmentDate && orderDetails.appointmentTime && !isTakingTrade) || (isTakingTrade && tradeOrder.appointmentDate && tradeOrder.appointmentTime) && (
            <>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ color: '#6b7280' }}>Appointment Date:</span>
                <span style={{ color: '#1f2937' }}>
                  {formatDate(new Date((isTakingTrade ? tradeOrder.appointmentDate : orderDetails.appointmentDate) + 'T00:00:00'))}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ color: '#6b7280' }}>Appointment Time:</span>
                <span style={{ color: '#1f2937' }}>
                  {isTakingTrade ? tradeOrder.appointmentTime : orderDetails.appointmentTime}
                </span>
              </div>
            </>
          )}
          
          {/* Drive-in instructions */}
          {orderDetails.isDriveIn && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <h4 style={{
                color: '#0c4a6e',
                marginBottom: '0.5rem',
                fontSize: '1rem'
              }}>🚗 Drive-In Instructions:</h4>
              <ul style={{
                color: '#0c4a6e',
                margin: 0,
                paddingLeft: '1.5rem'
              }}>
                <li>Drive to our location</li>
                <li>Wait in line</li>
                <li>Scan QR code at machine</li>
              </ul>
            </div>
          )}
          
          {/* Special appointment instructions */}
          {!orderDetails.isDriveIn && orderDetails.appointmentType === 'special' && (
            <div style={{
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <h4 style={{
                color: '#d97706',
                marginBottom: '0.5rem',
                fontSize: '1rem'
              }}>⭐ Special Appointment Instructions:</h4>
              <ul style={{
                color: '#d97706',
                margin: 0,
                paddingLeft: '1.5rem'
              }}>
                <li>Your appointment fee will not be refunded if you miss your appointment.</li>
                <li>Arrive up to 20 minutes early or 45 minutes late (affected by closing time)</li>
                <li>Rebook for free up to 6 hours before opening time on day of appointment.</li>
                <li>Cost of wash will be paid at location.</li>
              </ul>
            </div>
          )}
          
          {/* Free appointment instructions */}
          {!orderDetails.isDriveIn && orderDetails.appointmentType === 'free' && (
            <div style={{
              background: '#dbeafe',
              border: '1px solid #3b82f6',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <h4 style={{
                color: '#1e40af',
                marginBottom: '0.5rem',
                fontSize: '1rem'
              }}>📅 Normal Appointment Rules:</h4>
              <ul style={{
                color: '#1e40af',
                margin: 0,
                paddingLeft: '1.5rem'
              }}>
                <li>One free appointment per customer every 3 days</li>
                <li>Limited availability during peak hours</li>
              </ul>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '1rem',
            marginTop: '1rem'
          }}>
            <span style={{ fontWeight: 'bold', color: '#1f2937' }}>Subtotal:</span>
            <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#f97316' }}>
              ${isTakingTrade ? tradeOrder.price : (orderDetails.specialIsFree ? 0 : orderDetails.price)}
            </span>
          </div>
          

          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '0.5rem',
            marginTop: '0.5rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            <span style={{ fontWeight: 'bold', color: '#1f2937' }}>Total:</span>
            <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#f97316' }}>
              ${isTakingTrade ? tradeOrder.price : (orderDetails.specialIsFree ? 0 : orderDetails.price)}
            </span>
          </div>
        </div>
      )}
      

      
      {(() => {
                      const totalPrice = isTakingTrade ? tradeOrder.price : (orderDetails.specialIsFree ? 0 : orderDetails.price);
        const isFreeOrder = totalPrice === 0;
        
        return isFreeOrder ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              background: '#d1fae5',
              border: '1px solid #10b981',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{
                color: '#065f46',
                marginBottom: '1rem',
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}>
                🎉 Free Order!
              </h3>
              <p style={{
                color: '#065f46',
                marginBottom: '1.5rem',
                fontSize: '1rem'
              }}>
                Your order total is $0. No payment required.
              </p>
              <button 
                onClick={handleSubmit}
                style={{
                  padding: '1rem 2rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#059669'}
                onMouseOut={(e) => e.target.style.background = '#10b981'}
              >
                Complete Booking
              </button>
            </div>
          </div>
        ) : (
              <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="cardNumber" style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#1f2937',
              fontWeight: 'bold'
            }}>Card Number:</label>
            <input
              type="text"
              id="cardNumber"
              name="cardNumber"
              value={paymentData.cardNumber}
              onChange={handleInputChange}
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="cardholderName" style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#1f2937',
              fontWeight: 'bold'
            }}>Cardholder Name:</label>
            <input
              type="text"
              id="cardholderName"
              name="cardholderName"
              value={paymentData.cardholderName}
              onChange={handleInputChange}
              placeholder="John Doe"
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="expiryDate" style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#1f2937',
                fontWeight: 'bold'
              }}>Expiry Date:</label>
              <input
                type="text"
                id="expiryDate"
                name="expiryDate"
                value={paymentData.expiryDate}
                onChange={handleInputChange}
                placeholder="MM/YY"
                maxLength="5"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ flex: 1 }}>
              <label htmlFor="cvv" style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#1f2937',
                fontWeight: 'bold'
              }}>CVV:</label>
              <input
                type="text"
                id="cvv"
                name="cvv"
                value={paymentData.cvv}
                onChange={handleInputChange}
                placeholder="123"
                maxLength="4"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
          
          <button type="submit" style={{
            width: '100%',
            padding: '1rem',
            background: '#f97316',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.background = '#ea580c'}
          onMouseOut={(e) => e.target.style.background = '#f97316'}>
            Process Payment
          </button>
        </form>
        );
      })()}
    </div>
  );
};

export default Payment; 